import { useState, useEffect, useCallback } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { apiClient } from '@/services/apiClient';

export interface ManagerDashboardSummaryDto {
  currentWeek: {
    start: string;
    end: string;
  };
  teamThisWeek: {
    totalHours: number;
    billableHours: number;
    utilizationRate: number;
    trendPercentage: number;
    teamSize: number;
  };
  approvalsRequired: {
    pendingCount: number;
    overdueCount: number;
    pendingApprovals: PendingApprovalDto[];
  };
  teamPerformance: {
    utilizationRate: number;
    utilizationTarget: number;
    isAboveTarget: boolean;
    teamSize: number;
    trendPercentage: number;
  };
  missingTimesheets: {
    count: number;
    overdueCount: number;
  };
  recentTeamActivity: RecentTeamActivityDto[];
}

export interface RecentTeamActivityDto {
  employeeName: string;
  employeeId: number;
  action: string;
  description: string;
  type: 'submitted' | 'approved' | 'overdue';
  activityDate: string;
  totalHours?: number;
}

export interface PendingApprovalDto {
  timesheetId: number;
  employeeId: string;
  employeeName: string;
  weekEnding: string;
  submittedAt?: string;
  daysSinceSubmission: number;
  isOverdue: boolean;
}

export function useManagerDashboardSummary(screenId: string = 'homepage') {
  const { getScreenMode } = useDataSource();
  const [summary, setSummary] = useState<ManagerDashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const mode = getScreenMode(screenId);

  const fetchSummary = useCallback(async () => {
    if (mode !== 'live') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiClient.get<ManagerDashboardSummaryDto>('/dashboard/manager-summary');
      setSummary(data);
    } catch (err) {
      console.error('Error fetching manager homepage summary:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [mode, screenId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Refetch when the browser tab regains focus so stale data
  // (e.g. pending approvals that have since been actioned) is cleared
  // without a full page reload.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSummary();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSummary]);

  // Listen for company-changed event to refetch data when switching companies.
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        fetchSummary();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [fetchSummary]);

  return { summary, loading, error, mode, refetch: fetchSummary };
}
